import React from "react";
import {
  IconArrowsSort,
  IconFilter,
  IconTrashX,
  IconX,
} from "@tabler/icons-react";
import SortByPropertyBrowser, { SortProps } from "../SortByPropertyBrowser";
import FilterByPropertyBrowser, {
  FilterRule,
} from "../FilterByPropertyBrowser";
import { CollectionIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import GroupByPropertyBrowser from "../GroupByPropertyBrowser";
import { DropdownOption } from "components/DropdownBrowser";
import { observer } from "mobx-react";
import Tooltip from "components/Tooltip";
import { PropertyBrowserTreeNode } from "api-client";
import {
  EMPTY_FILTER_RULE,
  EMPTY_SORT_ROW,
  PROPERTY_RULE_OPERATORS,
} from "features/DynamicTable/consts";
import useDynamicTable from "features/DynamicTable/useDynamicTable";
import { dynamicTableStore } from "../../../DynamicTableStore.ts";

type FilterItem = SortProps | DropdownOption | FilterRule;

function AdvancedFilterView() {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const { clearGrouping } = useDynamicTable();

  const selectedItemStyle =
    "flex text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-3xl px-2 py-[1px] items-center mr-2 transition cursor-pointer";

  function filterItem(
    item: FilterItem,
    index: number,
    removeItem: (item: FilterItem) => void
  ) {
    return (
      <Tooltip
        key={index}
        content={displayFilterCondition(item as FilterRule)}
        truncate
      >
        <div className={selectedItemStyle} onClick={() => removeItem(item)}>
          {isEng
            ? (item as FilterRule).property?.name
            : (item as FilterRule).property?.name_de}
          <IconX
            size={18}
            stroke={3}
            className="ml-1.5 text-indigo-500 cursor-pointer"
          />
        </div>
      </Tooltip>
    );
  }

  function groupingItem(property: PropertyBrowserTreeNode) {
    const name = isEng ? property?.name : property?.name_de;
    return (
      <Tooltip key={property.id} content={name} truncate>
        <div
          className={selectedItemStyle}
          onClick={() =>
            dynamicTableStore.filterStore.removeGroupingRule(property.id)
          }
        >
          {name}
          <IconX size={18} stroke={3} className="ml-1.5 text-indigo-500" />
        </div>
      </Tooltip>
    );
  }

  function displayFilterCondition(filterItem: FilterRule) {
    const propertyName = isEng
      ? filterItem.property?.name
      : filterItem.property?.name_de;
    const operation = PROPERTY_RULE_OPERATORS.find(
      (e) => e.id == filterItem.operator
    );

    if (!operation) {
      return propertyName;
    }
    return `${propertyName} ${isEng ? operation.name : operation.name_de} ${
      filterItem.value
    }`;
  }

  function removeFilterItem(item: FilterItem) {
    const filteredValues =
      dynamicTableStore.filterStore.selectedFilterByItems.rules.filter(
        (rec) =>
          rec.property?.column_key !== (item as FilterRule).property?.column_key
      );
    dynamicTableStore.filterStore.setSelectedFilterByItems({
      ...dynamicTableStore.filterStore.selectedFilterByItems,
      rules: filteredValues.length
        ? filteredValues
        : [{ ...EMPTY_FILTER_RULE }],
    });
  }

  function removeSortItem(item: FilterItem) {
    const filteredValues =
      dynamicTableStore.filterStore.selectedSortedByItems.filter(
        (rec) =>
          rec.property?.column_key !== (item as SortProps).property?.column_key
      );
    dynamicTableStore.filterStore.setSelectedSortedByItems(
      filteredValues.length ? filteredValues : [{ ...EMPTY_SORT_ROW }]
    );
  }

  function groupByTile() {
    return (
      <div className="flex items-center font-semibold text-gray-500 border-b border-gray-100 py-1">
        <div className="flex rounded-sm bg-gray-500 text-white pr-1 py-1">
          <span className="w-6 flex justify-center">
            <CollectionIcon width={16} strokeWidth={3} />
          </span>
          <span>
            {dynamicTableStore.filterStore.validGroupByRules?.length
              ? `${t("mappingEditor.grouped")} ${t("mappingEditor.by")}: `
              : t("mappingEditor.group")}
          </span>
        </div>
        {dynamicTableStore.filterStore.selectedGroupByItems.length > 0 ? (
          <div className="flex ml-3 items-center">
            {dynamicTableStore.filterStore.selectedGroupByItems.map((item) =>
              groupingItem(item)
            )}
            <IconTrashX
              size={18}
              stroke={3}
              className="cursor-pointer transition hover:text-gray-900 mr-0.5"
              onClick={clearGrouping}
            />
          </div>
        ) : null}
        <GroupByPropertyBrowser advancedMode />
      </div>
    );
  }

  function filterByTile() {
    return (
      <div className="flex items-center font-semibold text-gray-500 border-b border-gray-100 py-1">
        <span className="w-6 flex justify-center">
          <IconFilter width={16} stroke={3} />
        </span>
        <span className="mr-2">
          {dynamicTableStore.filterStore.validFilterRules?.rules.length
            ? `${t("mappingEditor.filtered")} ${t("mappingEditor.by")}: `
            : t("mappingEditor.filter")}
        </span>
        {Number(dynamicTableStore.filterStore.validFilterRules?.rules?.length) >
        0 ? (
          <div className="flex ml-1 items-center">
            {dynamicTableStore.filterStore.selectedFilterByItems?.rules.map(
              (item, index) => {
                if (item.property && item.value) {
                  return filterItem(item, index, removeFilterItem);
                }
              }
            )}
            <IconTrashX
              size={18}
              stroke={3}
              className="cursor-pointer hover:text-gray-900 transition mr-0.5"
              onClick={() =>
                dynamicTableStore.filterStore.removeAllFilteringRules()
              }
            />
          </div>
        ) : null}
        <FilterByPropertyBrowser advancedMode />
      </div>
    );
  }

  function sortByTile() {
    return (
      <div className="flex items-center font-semibold text-gray-500 py-1">
        <span className="w-6 flex justify-center">
          <IconArrowsSort width={16} stroke={3} />
        </span>
        <span className="mr-2">
          {dynamicTableStore.filterStore.validSortingRules?.length
            ? `${t("mappingEditor.sorted")} ${t("mappingEditor.by")}: `
            : t("mappingEditor.sort")}
        </span>
        {Number(dynamicTableStore.filterStore.validSortingRules?.length) > 0 ? (
          <div className="flex ml-1 items-center">
            {dynamicTableStore.filterStore.selectedSortedByItems.map(
              (item, index) => {
                if (item.property)
                  return filterItem(item, index, removeSortItem);
              }
            )}
            <IconTrashX
              size={18}
              stroke={3}
              className="cursor-pointer hover:text-gray-900 transition mr-0.5"
              onClick={() =>
                dynamicTableStore.filterStore.removeAllSortingRules()
              }
            />
          </div>
        ) : null}
        <SortByPropertyBrowser advancedMode />
      </div>
    );
  }

  return (
    <div className="w-full pr-3">
      {groupByTile()}
      {filterByTile()}
      {sortByTile()}
    </div>
  );
}

export default observer(AdvancedFilterView);
